import { ParsedUrlQuery } from 'querystring'
import { GetStaticPropsResult, GetStaticPathsResult } from 'next'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanityCollectionPage } from '@data/sanity/queries/types/collection'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import { getCollectionPage } from '@lib/sanity/collection'
import { getAllSlugs } from '@lib/sanity/page'
import { getAllLocales, Locale } from '@lib/language'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface CollectionPageProps {
  locale: Locale
  page: SanityCollectionPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const CollectionPage = ({ page, site, reviews }: CollectionPageProps) => (
  <Layout page={page} site={site}>
    <Modules
      modules={page.modules}
      cartSettings={site.cart}
      collectionProducts={page.products}
      featuredProductIds={page.featuredProductIds}
      reviews={reviews}
    />
  </Layout>
)

interface StaticCollectionPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticCollectionPageParams>): Promise<
  GetStaticPropsResult<CollectionPageProps>
> {
  const collectionPage = await getCollectionPage(
    locale,
    params?.slug ?? '',
    getPreview(preview, previewData)
  )

  if (!collectionPage.page) {
    return { notFound: true }
  }

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    collectionPage.page.modules ?? [],
    shopifyDomain,
    collectionPage.site.settings.stampedApiKey
  )

  return {
    props: {
      locale,
      page: collectionPage.page,
      site: collectionPage.site,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticCollectionPageParams>
> {
  const allCollections = await getAllSlugs('collection')
  const allLocales = getAllLocales()
  const paths: StaticPathWithSlug[] = allCollections
    .map((collection) => ({
      ...collection,
      locale: collection.locale as Locale,
    }))
    .filter(({ locale }) => allLocales.includes(locale))
    .filter(({ slug }) => !!slug)
    .map(({ locale, slug }) => ({
      params: {
        slug,
      },
      locale,
    }))

  return {
    paths,
    fallback: 'blocking',
  }
}

export default CollectionPage
